import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetStockAdjustmentQuery } from "@/redux/services/inventory/stockAdjustment.api";
import { StockAdjustment } from "@/redux/services/inventory/inventory.type";
import {
  displayPrice,
  formatPhoneNumber,
  formatStock,
} from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import AdjustmentReasonBadge from "./AdjustmentReasonBadge";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";

interface AdjustmentDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  adjustmentId: string | null;
}

const formatQuantity = (quantity: string, unit: string) => {
  const parsed = parseFloat(quantity);
  const signClass =
    parsed < 0 ? "text-red-600" : parsed > 0 ? "text-green-600" : "";
  const prefix = parsed > 0 ? "+" : "";
  return (
    <span className={`${signClass} font-semibold text-lg`}>
      {prefix}
      {formatStock(quantity, unit)}
    </span>
  );
};

const renderReferenceBlock = (
  adj: StockAdjustment,
  onClose: () => void,
  onOrderClick: (id: string) => void,
  canViewPurchaseOrders: boolean
) => {
  if (adj.order) {
    const order = adj.order;
    return (
      <div className="dash__card p-3">
        <p className="text-xs text-muted-foreground mb-1">Commande liée</p>
        <button
          type="button"
          onClick={() => onOrderClick(order.id)}
          className="text-sm font-medium text-card-foreground hover:text-blue-700 hover:underline"
        >
          {order.orderNumber}
        </button>
      </div>
    );
  }
  if (adj.purchaseOrder) {
    return (
      <div className="dash__card p-3">
        <p className="text-xs text-muted-foreground mb-1">
          Bon de commande lié
        </p>
        {/* Purchase orders need suppliers.view (SFRESTO-165). */}
        {canViewPurchaseOrders ? (
          <Link
            to={`/inventory/purchase-orders/${adj.purchaseOrder.id}`}
            onClick={onClose}
            className="text-sm font-medium text-card-foreground hover:text-blue-700 hover:underline"
          >
            {adj.purchaseOrder.poNumber}
          </Link>
        ) : (
          <p className="text-sm font-medium text-card-foreground">
            {adj.purchaseOrder.poNumber}
          </p>
        )}
      </div>
    );
  }
  if (adj.stockCount) {
    return (
      <div className="dash__card p-3">
        <p className="text-xs text-muted-foreground mb-1">Inventaire lié</p>
        <Link
          to={`/inventory/stock-counts/${adj.stockCount.id}`}
          onClick={onClose}
          className="text-sm font-medium text-card-foreground hover:text-blue-700 hover:underline"
        >
          Voir le contrôle
        </Link>
      </div>
    );
  }
  return null;
};

const AdjustmentDetailsDrawer = ({
  isOpen,
  onClose,
  adjustmentId,
}: AdjustmentDetailsDrawerProps) => {
  const { data: adj, isLoading } = useGetStockAdjustmentQuery(
    { uuid: adjustmentId ?? "" },
    { skip: !adjustmentId }
  );
  const [orderDrawerId, setOrderDrawerId] = useState<string | null>(null);
  const currency = useRestaurantCurrency();
  const { can } = usePermissions();

  return (
    <>
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 overflow-y-auto"
      >
        <div className="p-6 space-y-5">
          {isLoading || !adj ? (
            <div className="space-y-3">
              <Skeleton className="w-3/4 h-6" />
              <Skeleton className="w-full h-24" />
              <Skeleton className="w-full h-20" />
            </div>
          ) : (
            <>
              <SheetHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <SheetTitle>{adj.ingredient.name}</SheetTitle>
                  <AdjustmentReasonBadge reason={adj.reason} />
                </div>
                <SheetDescription>
                  {format(parseISO(adj.createdAt), "EEEE d MMMM yyyy, HH:mm", {
                    locale: fr,
                  })}
                </SheetDescription>
              </SheetHeader>

              {adj.reason === "cost_correction" ? (
                <div className="dash__card p-4">
                  <p className="text-xs text-muted-foreground">
                    Nouveau coût par unité
                  </p>
                  <p className="mt-1 font-semibold text-lg">
                    {adj.newCostPerUnit !== null
                      ? displayPrice(adj.newCostPerUnit, currency)
                      : "—"}
                  </p>
                </div>
              ) : (
                <div className="dash__card p-4">
                  <p className="text-xs text-muted-foreground">Quantité</p>
                  <div className="mt-1">
                    {adj.packaging && adj.packageQuantity !== null
                      ? formatQuantity(
                          adj.packageQuantity,
                          adj.packaging.name
                        )
                      : formatQuantity(adj.quantity, adj.ingredient.baseUnit)}
                  </div>
                  {adj.packaging && adj.packageQuantity !== null && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      = {formatStock(adj.quantity, adj.ingredient.baseUnit)}
                    </p>
                  )}
                </div>
              )}

              <div className="dash__card p-3">
                <p className="text-xs text-muted-foreground mb-1">Auteur</p>
                {adj.adjustedBy ? (
                  <div>
                    <p className="text-sm font-medium text-card-foreground">
                      {adj.adjustedBy.fullName}
                    </p>
                    {adj.adjustedBy.phone && (
                      <a
                        href={`tel:${adj.adjustedBy.phone}`}
                        className="text-xs text-card-foreground hover:text-blue-700"
                      >
                        {formatPhoneNumber(adj.adjustedBy.phone)}
                      </a>
                    )}
                  </div>
                ) : (
                  <p className="text-sm italic text-muted-foreground">
                    Système
                  </p>
                )}
              </div>

              {renderReferenceBlock(
                adj,
                onClose,
                setOrderDrawerId,
                can("suppliers.view")
              )}

              {adj.notes && (
                <div className="dash__card p-3">
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{adj.notes}</p>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
    <OrderDetailsDrawer
      orderId={orderDrawerId ?? ""}
      isOpen={!!orderDrawerId}
      onClose={() => setOrderDrawerId(null)}
    />
    </>
  );
};

export default AdjustmentDetailsDrawer;
